import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import LabeledInput from '../LabeledInput';
import { GroupInputs } from '../GroupInputs';
import { SelectInput } from '../SelectInput';
import { TextInput } from '../TextInput';

import * as api from '../../api';
import * as logger from '../../logger';

import styles from '../utils.module.css';
import { getOptionsFromRecord } from '../utils';

interface Props {
  value: string;
  updateFieldValue: (value: string) => void;
  saveFieldValue: (value: string) => void;
  required: boolean;
}

const sexualOrientationValues = getOptionsFromRecord(
  api.Previsit.sexualOrientationMap,
);

const sexualIdentityValues = getOptionsFromRecord(
  api.Previsit.sexualIdentityMap,
);

const sexAtBirthValues = getOptionsFromRecord(api.Previsit.sexAtBirthMap);

const sexualPronounValues = getOptionsFromRecord(api.Previsit.sexualPronounMap);

export const SexualIdentity: React.FC<Props> = ({
  value,
  updateFieldValue,
  saveFieldValue,
  required,
}) => {
  const { t } = useTranslation();
  const [sexualIdentity, setSexualIdentity] =
    useState<api.PatientSocialHistory.SexualIdentityFieldValue>({});

  const saveFormField = (update: {
    [key in keyof api.PatientSocialHistory.SexualIdentityFieldValue]?: string;
  }) => {
    const updatedValues = JSON.stringify({
      ...sexualIdentity,
      ...(update.orientation &&
        update.orientation !==
          api.PatientSocialHistory.SexualOrientation.Other && {
          orientationRaw: undefined,
        }),
      ...(update.identity &&
        update.identity !== api.PatientSocialHistory.SexualIdentity.Other && {
          identityRaw: undefined,
        }),
      ...(update.sexAtBirth &&
        update.sexAtBirth !== api.PatientSocialHistory.SexAtBirth.Other && {
          sexAtBirthRaw: undefined,
        }),
      ...(update.pronouns &&
        update.pronouns !== api.PatientSocialHistory.SexualPronoun.Other && {
          pronounsRaw: undefined,
        }),
      ...update,
    });
    updateFieldValue(updatedValues);
    saveFieldValue(updatedValues);
  };

  useEffect(() => {
    try {
      if (!value) return;
      const parsedValue: api.PatientSocialHistory.SexualIdentityFieldValue =
        JSON.parse(value);
      setSexualIdentity(parsedValue);
    } catch (e) {
      logger.error(e, 'Error parsing sexual identity value');
    }
  }, [value]);

  return (
    <GroupInputs>
      <LabeledInput
        label={t('What is your sexual orientation?')}
        htmlFor="sexual-identity-orientation"
      >
        <SelectInput
          id="sexual-identity-orientation"
          value={sexualIdentity.orientation || ''}
          onChange={(orientation: string) => {
            saveFormField({ orientation });
          }}
          options={sexualOrientationValues}
          isOptional={!required}
        />
        {sexualIdentity.orientation ===
          api.PatientSocialHistory.SexualOrientation.Other && (
          <div className={styles.SmallMarginTop}>
            <TextInput
              id="sexual-identity-orientation-raw"
              value={sexualIdentity.orientationRaw || ''}
              className={styles.SmallMarginTop}
              onBlur={() => {
                saveFormField({
                  orientationRaw: sexualIdentity.orientationRaw || '',
                });
              }}
              onChange={(
                event: React.ChangeEvent<
                  HTMLTextAreaElement | HTMLInputElement
                >,
              ) => {
                updateFieldValue(
                  JSON.stringify({
                    ...sexualIdentity,
                    orientationRaw: event.target.value,
                  }),
                );
              }}
            />
          </div>
        )}
      </LabeledInput>
      <LabeledInput
        label={t('What is your gender identity?')}
        htmlFor="sexual-identity-gender"
      >
        <SelectInput
          id="sexual-identity-gender"
          value={sexualIdentity.identity || ''}
          onChange={(identity: string) => {
            saveFormField({ identity });
          }}
          options={sexualIdentityValues}
          isOptional={!required}
        />
        {sexualIdentity.identity ===
          api.PatientSocialHistory.SexualIdentity.Other && (
          <div className={styles.SmallMarginTop}>
            <TextInput
              id="sexual-identity-gender-raw"
              value={sexualIdentity.identityRaw || ''}
              className={styles.SmallMarginTop}
              onBlur={() => {
                saveFormField({
                  identityRaw: sexualIdentity.identityRaw || '',
                });
              }}
              onChange={(
                event: React.ChangeEvent<
                  HTMLTextAreaElement | HTMLInputElement
                >,
              ) => {
                updateFieldValue(
                  JSON.stringify({
                    ...sexualIdentity,
                    identityRaw: event.target.value,
                  }),
                );
              }}
            />
          </div>
        )}
      </LabeledInput>
      <LabeledInput
        label={t('What was your assigned sex at birth?')}
        htmlFor="sexual-identity-sex-at-birth"
      >
        <SelectInput
          id="sexual-identity-sex-at-birth"
          value={sexualIdentity.sexAtBirth || ''}
          onChange={(sexAtBirth: string) => {
            saveFormField({ sexAtBirth });
          }}
          options={sexAtBirthValues}
          isOptional={!required}
        />
        {sexualIdentity.sexAtBirth ===
          api.PatientSocialHistory.SexAtBirth.Other && (
          <div className={styles.SmallMarginTop}>
            <TextInput
              id="sexual-identity-sex-at-birth-raw"
              value={sexualIdentity.sexAtBirthRaw || ''}
              className={styles.SmallMarginTop}
              onBlur={() => {
                saveFormField({
                  sexAtBirthRaw: sexualIdentity.sexAtBirthRaw || '',
                });
              }}
              onChange={(
                event: React.ChangeEvent<
                  HTMLTextAreaElement | HTMLInputElement
                >,
              ) => {
                updateFieldValue(
                  JSON.stringify({
                    ...sexualIdentity,
                    sexAtBirthRaw: event.target.value,
                  }),
                );
              }}
            />
          </div>
        )}
      </LabeledInput>
      <LabeledInput
        label={t('What are your preferred pronouns?')}
        htmlFor="sexual-identity-pronouns"
      >
        <SelectInput
          id="sexual-identity-pronouns"
          value={sexualIdentity.pronouns || ''}
          onChange={(pronouns: string) => {
            saveFormField({ pronouns });
          }}
          options={sexualPronounValues}
          isOptional={!required}
        />
        {sexualIdentity.pronouns ===
          api.PatientSocialHistory.SexualPronoun.Other && (
          <div className={styles.SmallMarginTop}>
            <TextInput
              id="sexual-identity-spronouns-raw"
              value={sexualIdentity.pronounsRaw || ''}
              onBlur={() => {
                saveFormField({
                  pronounsRaw: sexualIdentity.pronounsRaw || '',
                });
              }}
              onChange={(
                event: React.ChangeEvent<
                  HTMLTextAreaElement | HTMLInputElement
                >,
              ) => {
                updateFieldValue(
                  JSON.stringify({
                    ...sexualIdentity,
                    pronounsRaw: event.target.value,
                  }),
                );
              }}
            />
          </div>
        )}
      </LabeledInput>
    </GroupInputs>
  );
};
